// The organization the user is currently acting as.
//
// Scout and IDX share one Auth0 tenant, one client and one API audience, so the
// token alone cannot say which organization a request is for - a user may belong
// to several, and to different ones in each product. Every request therefore
// carries the choice explicitly, in the X-Vectech-Org header, and the backend
// validates it against the memberships in the token.
//
// Stored per browser rather than per session so a refresh does not silently
// bounce the user back to their default organization.
//
// Returning undefined is a valid, normal state: it means "no explicit choice",
// and the backend then resolves the user's own organization exactly as it always
// has. That is what makes sending this header a no-op for anyone in a single
// organization, and what lets it ship before an organization selector exists.

// The product this app is. Membership is scoped per product - a user can be in
// different organizations in IDX and Scout - so the stored selection is too, or
// the two apps would overwrite each other's choice in the same browser.
export const PRODUCT = "IDX";

const STORAGE_KEY = `vectech.${PRODUCT}.activeOrg`;

export const ORG_HEADER = "X-Vectech-Org";

// localStorage throws outright in some privacy modes rather than returning null,
// so every access is guarded. Losing the selection is recoverable; a crash in
// the request layer is not.
export const getActiveOrg = () => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value && value.trim() ? value.trim() : undefined;
  } catch (e) {
    return undefined;
  }
};

export const setActiveOrg = (org) => {
  try {
    if (org && String(org).trim()) {
      window.localStorage.setItem(STORAGE_KEY, String(org).trim());
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    // Ignored: the selection simply will not persist across a refresh.
  }
};

export const clearActiveOrg = () => setActiveOrg(undefined);

/**
 * The org header, or an empty object when no organization has been chosen.
 *
 * Spread into a request's headers. Deliberately omits the header rather than
 * sending an empty one - an empty value would be a claim ("act as no
 * organization") rather than the absence of one.
 */
export const activeOrgHeader = () => {
  const org = getActiveOrg();
  return org ? { [ORG_HEADER]: org } : {};
};

// The Auth0 rule puts the whole app_metadata object on the ID token as well as
// the access token, so memberships are readable client-side with no request.
//
// That matters more than it looks: the selection has to be reconciled BEFORE the
// first API call, because a stale organization would be refused with a 403 and
// the endpoint that would tell us the valid list is itself refused. Reading the
// token breaks that deadlock.
export const APP_METADATA_CLAIM = "https://mos-id.com/app_metadata";

/**
 * Superusers may act as any organization - a server-side privilege.
 *
 * Read from app_metadata.superUser, as the backends read it. Only the boolean
 * true counts.
 */
export const readSuperUser = (user) =>
  user?.[APP_METADATA_CLAIM]?.superUser === true;

/**
 * The caller's memberships for THIS product, from the Auth0 user object.
 *
 * An entry with no product is treated as granting every product, matching the
 * backend - users backfilled before the field existed must not lose access.
 */
export const readMembershipsForProduct = (user) => {
  const raw = user?.[APP_METADATA_CLAIM]?.memberships;
  if (!Array.isArray(raw)) return [];
  const wanted = PRODUCT.toLowerCase();
  return raw
    .filter((m) => m && typeof m.org === "string" && m.org.trim())
    .filter(
      (m) => !m.product || String(m.product).toLowerCase() === wanted
    )
    .map((m) => ({
      org: m.org.trim(),
      role: typeof m.role === "string" && m.role.trim() ? m.role.trim() : "member",
    }))
    .filter(
      (m, i, all) => all.findIndex((o) => o.org === m.org) === i
    );
};

/**
 * Bring the stored selection in line with what the user may actually act as.
 *
 * Empty, or naming an organization they are not in, becomes the first of their
 * memberships. No memberships for this product becomes empty - which is not a
 * failure, it is what an orgless or Scout-only user legitimately is, and the
 * backend then answers from the token alone as it always did.
 *
 * A superuser is exempt from the "must be one of yours" rule, because they may
 * act as any organization. Without that exemption their selection would be
 * reverted by the reload that switching performs - they would pick another
 * tenant, the page would reload, and reconcile would put them straight back on
 * their own membership. They still get a default when nothing is stored.
 *
 * Returns the organization now in effect, or undefined.
 */
export const reconcileActiveOrg = (memberships, { superUser = false } = {}) => {
  const orgs = (memberships || []).map((m) => m.org);
  const current = getActiveOrg();

  if (superUser && current) return current;

  if (orgs.length === 0) {
    clearActiveOrg();
    return undefined;
  }

  if (current && orgs.includes(current)) return current;

  setActiveOrg(orgs[0]);
  return orgs[0];
};
